import { useState } from "react";
import { Download, FileText, ListChecks, Mic, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/Status";
import { useLang } from "@/i18n";
import { api, downloadBlob, errCode } from "@/lib/api";

const PRIO_CLS = {
  high: "border-vox-rec/40 text-[#ff9b9e]",
  medium: "border-vox-amber/40 text-vox-amber",
  low: "border-vox-line text-vox-muted",
};

const Block = ({ title, children, testId }) => (
  <div data-testid={testId}>
    <div className="mono-label mb-3">{title}</div>
    {children}
  </div>
);

const SummaryList = ({ items, none, testId, marker }) => (
  items?.length ? (
    <ul className="space-y-1.5" data-testid={testId}>
      {items.map((x, i) => <li key={i} className="flex gap-2 text-sm leading-relaxed text-vox-text"><span className="shrink-0 font-mono text-vox-bright">{marker || "—"}</span><span>{x}</span></li>)}
    </ul>
  ) : <p className="text-sm text-vox-dim" data-testid={testId}>{none}</p>
);

function NegotiationSummary({ p }) {
  const { t } = useLang();
  const has = p.about || p.questions_discussed?.length || p.conclusions?.length || p.action_items?.length;
  if (!has) return null;
  const Cell = ({ label, children }) => (
    <div className="p-5 md:p-6">
      <div className="mb-3 font-mono text-[11px] uppercase tracking-wider text-vox-bright">{label}</div>
      {children}
    </div>
  );
  return (
    <section className="overflow-hidden rounded-2xl border border-vox-bright/30 bg-[#D4ECEF]/[0.04]" data-testid="negotiation-summary">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-vox-bright/20 px-5 py-3 md:px-6">
        <span className="flex items-center gap-2 text-sm font-semibold text-vox-text"><Sparkles size={14} className="text-vox-bright" /> {t("detail.summary")}</span>
        <span className="font-mono text-[10px] uppercase tracking-wider text-vox-dim">{t("detail.summaryHint")}</span>
      </div>
      <div className="grid divide-y divide-vox-bright/15 md:grid-cols-2 md:divide-y-0 md:[&>*:nth-child(odd)]:border-r md:[&>*:nth-child(-n+2)]:border-b md:[&>*]:border-vox-bright/15">
        <Cell label={t("detail.about")}><p className="text-sm leading-relaxed text-vox-text" data-testid="summary-about">{p.about || p.brief_summary || "—"}</p></Cell>
        <Cell label={t("detail.questions")}><SummaryList items={p.questions_discussed} none={t("detail.none")} testId="summary-questions" marker="?" /></Cell>
        <Cell label={`${t("detail.assignments")} (${p.action_items?.length || 0})`}>
          <SummaryList items={(p.action_items || []).map((a) => `${a.assignee || "—"}: ${a.task}${a.deadline ? ` (${a.deadline})` : ""}`)} none={t("detail.none")} testId="summary-assignments" marker="→" />
        </Cell>
        <Cell label={t("detail.conclusions")}><SummaryList items={p.conclusions} none={t("detail.none")} testId="summary-conclusions" marker="✓" /></Cell>
      </div>
    </section>
  );
}

export function ProtocolView({ job, onChanged }) {
  const { t } = useLang();
  const [tab, setTab] = useState("protocol");
  const p = job.protocol || {};
  const busy = !["done", "error"].includes(job.status);

  const onDownload = async () => {
    try {
      await downloadBlob(`/ai/jobs/${job.id}/docx`, `protocol_${job.id}.docx`);
    } catch (e) {
      toast.error(t(`errors.${errCode(e)}`, errCode(e)));
    }
  };

  const onResummarize = async () => {
    if (!window.confirm(t("detail.resummarizeConfirm"))) return;
    try {
      await api.post(`/ai/jobs/${job.id}/resummarize`);
      onChanged?.({ status: "summarizing", progress: 60 });
      toast.success(t("detail.resummarizeStarted"));
    } catch (e) {
      toast.error(t(`errors.${errCode(e)}`, errCode(e)));
    }
  };

  const onRetry = async () => {
    try {
      await api.post(`/ai/jobs/${job.id}/retry`);
      onChanged?.({ status: "queued", progress: 5, error: null });
      toast.success(t("detail.retryStarted"));
    } catch (e) {
      toast.error(t(`errors.${errCode(e)}`, errCode(e)));
    }
  };

  return (
    <div className="panel p-6 md:p-8" data-testid="protocol-view">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mono-label mb-2">{t(`detail.source.${job.source}`, job.source)}</div>
          <h2 className="text-xl font-semibold text-vox-text">{job.title}</h2>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-vox-dim">
            <StatusBadge status={job.status} />
            {job.duration ? <span className="font-mono">{t("detail.duration")}: {Math.round(job.duration / 60)} {t("detail.min")}</span> : null}
            {job.size_mb ? <span className="font-mono">{job.size_mb} MB</span> : null}
            {job.model_info?.llm && <span className="font-mono">{t("detail.model")}: {job.model_info.stt} · {job.model_info.llm}</span>}
          </div>
        </div>
        {job.status === "done" && (
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={onResummarize} className="btn-ghost" title={t("detail.resummarizeHint")} data-testid="resummarize-button">
              <Sparkles size={14} /> {t("detail.resummarize")}
            </button>
            <button type="button" onClick={onDownload} className="btn-primary" data-testid="export-docx-download-button">
              <Download size={16} /> {t("detail.docx")}
            </button>
          </div>
        )}
      </div>

      {busy && (
        <div className="mt-6" data-testid="ai-processing-progress-bar">
          <Progress value={job.progress || 5} className="h-1.5 bg-vox-card" />
          <p className="mt-2 font-mono text-xs text-vox-muted">{t(`detail.status.${job.status}`)} · {job.progress || 5}%</p>
        </div>
      )}
      {job.status === "error" && (
        <div className="mt-6 space-y-3">
          <div className="rounded-xl border border-vox-rec/30 bg-vox-rec/10 p-4 text-sm text-[#ff9b9e]" data-testid="job-error">{job.error}</div>
          <button type="button" onClick={onRetry} className="btn-ghost" data-testid="retry-job-button">
            <RotateCcw size={14} /> {t("detail.retry")}
          </button>
        </div>
      )}
      {job.status === "done" && job.missing_chunks > 0 && (
        <div className="mt-6 rounded-xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm text-amber-200" data-testid="job-missing-chunks">
          {t("detail.missingChunks", null, { n: job.missing_chunks, sec: job.missing_chunks * 10 })}
        </div>
      )}

      {job.status === "done" && (
        <>
          <div className="mt-8 flex gap-1 rounded-full border border-vox-line bg-vox-deep/60 p-1 w-fit">
            {["protocol", "transcript"].map((k) => (
              <button key={k} type="button" onClick={() => setTab(k)} data-testid={`tab-${k}`}
                className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm transition-colors duration-200 ${tab === k ? "bg-vox-card text-vox-text" : "text-vox-dim hover:text-vox-text"}`}>
                {k === "protocol" ? <FileText size={14} /> : <Mic size={14} />}
                {t(`detail.${k}`)}
              </button>
            ))}
          </div>

          {tab === "protocol" && (
            <div className="mt-8 space-y-8" data-testid="protocol-summary-section">
              <NegotiationSummary p={p} />
              <Block title={t("detail.brief")}>
                <p className="text-base leading-relaxed text-vox-text">{p.brief_summary || "—"}</p>
              </Block>
              {p.topics?.length > 0 && (
                <Block title={t("detail.topics")}>
                  <div className="flex flex-wrap gap-2">
                    {p.topics.map((x, i) => <span key={i} className="rounded-full border border-vox-line px-3 py-1 text-xs text-vox-muted">{x}</span>)}
                  </div>
                </Block>
              )}
              <Block title={t("detail.detailed")}>
                <p className="whitespace-pre-line text-sm leading-relaxed text-vox-muted">{p.detailed_summary || "—"}</p>
              </Block>
              {p.key_decisions?.length > 0 && (
                <Block title={t("detail.decisions")}>
                  <ul className="space-y-2">{p.key_decisions.map((d, i) => <li key={i} className="flex gap-3 text-sm text-vox-text"><span className="font-mono text-vox-accent">{String(i + 1).padStart(2, "0")}</span>{d}</li>)}</ul>
                </Block>
              )}
              {p.open_questions?.length > 0 && (
                <Block title={t("detail.open")}>
                  <ul className="space-y-2">{p.open_questions.map((q, i) => <li key={i} className="flex gap-3 text-sm text-vox-muted"><span className="font-mono text-vox-amber">?</span>{q}</li>)}</ul>
                </Block>
              )}
              <Block title={<span className="flex items-center gap-2"><ListChecks size={14} /> {t("detail.tasks")} ({p.action_items?.length || 0})</span>}>
                {p.action_items?.length ? (
                  <div className="overflow-x-auto rounded-xl border border-vox-line">
                    <table className="w-full text-sm" data-testid="protocol-tasks-table">
                      <thead className="bg-vox-deep/60 font-mono text-[11px] uppercase tracking-wider text-vox-dim">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium">{t("detail.assignee")}</th>
                          <th className="px-4 py-3 text-left font-medium">{t("detail.task")}</th>
                          <th className="px-4 py-3 text-left font-medium">{t("detail.deadline")}</th>
                          <th className="px-4 py-3 text-left font-medium">{t("detail.priority")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {p.action_items.map((a, i) => (
                          <tr key={i} className="border-t border-vox-line" data-testid="protocol-task-row">
                            <td className="px-4 py-3 font-medium text-vox-text">{a.assignee || "—"}</td>
                            <td className="px-4 py-3 text-vox-muted">{a.task}</td>
                            <td className="px-4 py-3 font-mono text-xs text-vox-muted">{a.deadline || "—"}</td>
                            <td className="px-4 py-3"><span className={`rounded-full border px-2 py-0.5 font-mono text-[11px] uppercase ${PRIO_CLS[a.priority] || PRIO_CLS.low}`}>{t(`detail.prio.${a.priority}`, a.priority || "—")}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <p className="text-sm text-vox-dim">{t("detail.noTasks")}</p>}
              </Block>
            </div>
          )}

          {tab === "transcript" && (
            <div className="mt-8 space-y-3" data-testid="transcript-container">
              {(job.segments?.length ? job.segments : null)?.map((s, i) => (
                <div key={i} className="grid grid-cols-[64px_1fr] gap-4 border-b border-vox-line/60 pb-3 last:border-0">
                  <span className="font-mono text-xs text-vox-dim">{fmt(s.start)}</span>
                  <div>
                    <span className="mr-2 font-medium text-vox-bright" data-testid="transcript-speaker-tag">{s.speaker}</span>
                    <span className="text-sm text-vox-text">{s.text}</span>
                  </div>
                </div>
              )) || <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-vox-muted">{job.transcript_formatted}</pre>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const fmt = (s) => `${String(Math.floor((s || 0) / 60)).padStart(2, "0")}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;
